import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from '../../../../../utils/component-helpers';
import { bindActionCreators } from 'redux';
import { withResource } from '@cube3/state/src/redux/components/withResource';

// forms
import {
  reduxForm,
  getFormValues,
  InjectedFormProps,
  clearFields,
  arrayRemoveAll,
  Field
} from 'redux-form';

// UI

import { useModalActions } from '../../Modals/modalActions';
import AddProjectMembersModalUI from '@cube3/ui/src/Projects/Modals/AddProjectMembersModalUI';
import { PROJECT_ROLE_TYPES } from '@cube3/common/model/resource-types';
import { ClearInputHandler } from '@cube3/ui/src/forms/textfields/AutoCompleteInput';
import { isRequired } from '../../../../forms/helpers/validators';
import {
  ProjectAddAccountsField,
  ProjectAddMembersField
} from '../ProjectAddMembersField';
import { withProjectTeamMemberships } from '../HOCs/withProjectTeamMemberships';
import { withCreateProjectMember } from '../HOCs/withCreateProjectMember';
import {
  RequestStatuses,
  statuses
} from '@cube3/state/src/redux/ducks/request-status';
import { Project } from '@cube3/common/model/schema/resources/project';
import { Select } from '@cube3/cubicle/src/core/atoms/Select/Select';
import { Option } from '@cube3/cubicle/src/core/atoms/Select/Option';
import { HBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { SuggestionItem } from '../../Contracts/prefabs/MultipleResourcePicker/SuggestionsPickerBase';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';

interface ModalReceivedProps {
  modalContext: {
    hasTeamFeature: boolean;
    projectId: string;
  };
}
interface InjectedHOCProps {
  addMembers: (res) => string;
  addMembersStatus: RequestStatuses;
  addTeamAsMember: (res) => string;
  addTeamAsMemberStatus: RequestStatuses;
  retrievedProject: Project;
  loading: boolean;
}
interface ReduxFormState extends InjectedFormProps {
  onChange?: Function;
  clearEmailInput: ClearInputHandler;
  dispatch?: Function;
  formValues: {
    accounts: SuggestionItem[];
    role: (typeof PROJECT_ROLE_TYPES)[keyof typeof PROJECT_ROLE_TYPES];
  };
}

type Props = ModalReceivedProps & InjectedHOCProps & ReduxFormState;

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      clearEmailInput: clearFields
    },
    dispatch
  );
};

const mapStateToProps = (state, ownProps: Props) => {
  const values = getFormValues('ProjectMemberPicker')(state);
  return {
    form: ownProps.form,
    formValues: values,
    initialValues: {
      accounts: [],
      role: memberTypeOptions[1].id
    }
  };
};

const memberTypeOptions = [
  { display_name: 'Admin', id: PROJECT_ROLE_TYPES.Admin },
  { display_name: 'Member', id: PROJECT_ROLE_TYPES.Member }
];

function ProjectMemberPicker(props: Props) {
  const {
    form,
    handleSubmit,
    submitting,
    submitSucceeded,
    dispatch,
    valid,
    formValues,
    loading,
    anyTouched,
    retrievedProject,
    addTeamAsMemberStatus,
    modalContext: { hasTeamFeature, projectId }
  } = props;

  const { previousModal } = useModalActions();

  const { resources: projectMembers, loading: loadingMembers } =
    useResourceList__ALPHA({
      resourceType: projectId ? 'project' : undefined,
      resourceId: projectId,
      edgeType: projectId ? 'project-member' : undefined,
      edgeLabel: 'members'
    });

  const excludedAccounts = useMemo(() => {
    if (!projectMembers) return [];
    return projectMembers.map((p) => p.relationships.account.id);
  }, [projectMembers]);

  const isSubmitDisabled = !valid || !formValues?.accounts?.length;

  const closeModalHandler = React.useCallback(() => {
    previousModal();
    dispatch(arrayRemoveAll('ProjectMemberPicker', 'accounts'));
  }, [dispatch, previousModal]);

  useEffect(() => {
    if (submitSucceeded || addTeamAsMemberStatus === statuses.SUCCESS) {
      closeModalHandler();
    }
  }, [submitSucceeded, addTeamAsMemberStatus]);

  return (
    <AddProjectMembersModalUI
      accountBoxRenderer={
        <>
          {hasTeamFeature ? (
            <ProjectAddMembersField
              projectId={retrievedProject?.id}
              form={form}
              loading={loading || loadingMembers}
              autoFocus={true}
              excludedAccounts={excludedAccounts}
              scrollbarHeight={360}
            />
          ) : (
            <ProjectAddAccountsField
              form={form}
              autoFocus={true}
              loading={loading || loadingMembers}
              excludedAccounts={excludedAccounts}
              scrollbarHeight={360}
            />
          )}
          <Field
            name="role"
            placeholder="Select role..."
            component={WrappedSelect}
            props={{
              label: 'Role',
              options: memberTypeOptions,
              showErrors: anyTouched ? true : 'touched'
            }}
            validate={isRequired}
          />
        </>
      }
      onSubmit={handleSubmit}
      submitButtonDisabled={submitting || isSubmitDisabled}
      onCloseEvent={() => closeModalHandler()}
      loading={loading || submitting}
    />
  );
}

const WrappedSelect = (props) => {
  const { options, input, ...restProps } = props;

  return (
    <HBox align="center" justify="start" gap={16} style={{ marginTop: 16 }}>
      <Typography colorVariant="02">Role</Typography>
      <Select {...restProps} {...input} label={undefined} width={40}>
        {options.map((opt) => (
          <Option
            {...opt}
            key={opt.id || opt.display_name}
            value={opt.id || opt.display_name}
          >
            {opt.display_name}
          </Option>
        ))}
      </Select>
    </HBox>
  );
};

export default compose(ProjectMemberPicker)(
  // get the project to be able to mutate it
  withResource<{ modalContext: { projectId: string } }>({
    resourceType: 'project',
    resourceId: ({ modalContext }) => modalContext?.projectId,
    mapper: 'retrievedProject'
  }),
  withCreateProjectMember,
  withProjectTeamMemberships,
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: 'ProjectMemberPicker',
    destroyOnUnmount: true,
    validate: (values: any, props: any) => {
      return {};
    },
    onSubmit: (values, dispatch, props: Props) => {
      const accounts = values.accounts.filter((res) => res.type === 'account');
      const teams = values.accounts.filter((res) => res.type === 'team');

      props.addMembers(
        accounts.map((acc) => ({
          type: 'project-member',
          project_member_role: values.role,
          relationships: {
            account: {
              data: { id: acc.id, type: 'account' }
            }
          }
        }))
      );

      if (props.modalContext?.hasTeamFeature && teams.length > 0) {
        props.addTeamAsMember(
          teams.map((team) => ({
            type: 'project-team-membership',
            project_member_role: values.role,
            relationships: {
              project: {
                data: { type: 'project', id: props.retrievedProject.id }
              },
              team: {
                data: { id: team.id, type: 'team' }
              }
            }
          }))
        );
      }

      return new Promise((res) => null);
    }
  })
);
